import { useState, type CSSProperties, type FormEvent } from 'react'
import { changePassword, type AuthUser } from '../api/auth'
import { TENANT } from '../tenant'

// Forced on first login when the seat still has its admin-issued TEMPORARY password
// (user.must_change). The dashboard doesn't mount until a real password is set — the
// seat verifies the temp one and picks its own. Same gate visuals as Login.
export function ChangePassword({ user, onDone }: { user: AuthUser; onDone: (u: AuthUser) => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    if (next.length < 8) {
      setError('The new password must be at least 8 characters.')
      return
    }
    if (next !== confirm) {
      setError('The new passwords do not match.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const fresh = await changePassword(current, next)
      onDone(fresh)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the password.')
      setBusy(false)
    }
  }

  return (
    <div style={S.ground}>
      <div style={S.stack}>
        <img src="/minprilla-logo.png" alt={TENANT.productName} style={S.brandLogo} />
        <form style={S.card} onSubmit={submit}>
          <h1 style={S.title}>Set your password</h1>
          <p style={S.intro}>
            Welcome, {user.name}. You’re signed in with a temporary password — choose your own to continue.
          </p>

          <label style={S.label} htmlFor="cp-current">Temporary password</label>
          <input
            id="cp-current"
            style={S.input}
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            autoFocus
            required
          />

          <label style={S.label} htmlFor="cp-new">New password</label>
          <input
            id="cp-new"
            style={S.input}
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />

          <label style={S.label} htmlFor="cp-confirm">Confirm new password</label>
          <input
            id="cp-confirm"
            style={S.input}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />

          {error && <p style={S.error} role="alert">{error}</p>}

          <button style={{ ...S.button, ...(busy ? S.buttonBusy : {}) }} type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save and continue'}
          </button>
          <p style={S.foot}>At least 8 characters. You’ll use this password from now on.</p>
        </form>
      </div>
    </div>
  )
}

const S: Record<string, CSSProperties> = {
  ground: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--brand-dark)', padding: 24 },
  stack: { width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 },
  brandLogo: { height: 42, width: 'auto', display: 'block' },
  card: { width: '100%', background: 'var(--surface, #fff)', borderRadius: 16, padding: '32px 32px', display: 'flex', flexDirection: 'column', gap: 8, boxShadow: '0 24px 60px rgba(0,0,0,0.32)' },
  title: { margin: '0 0 2px', fontSize: 20, fontWeight: 700, color: 'var(--text, #1a1a1a)' },
  intro: { margin: '0 0 10px', fontSize: 13.5, color: 'var(--muted, #555)', lineHeight: 1.5 },
  label: { fontSize: 12, fontWeight: 600, color: 'var(--muted, #4a4a4a)', marginTop: 6 },
  input: { height: 42, padding: '0 12px', borderRadius: 9, border: '1px solid var(--line, #e1e1de)', fontSize: 15, outlineColor: 'var(--accent)' },
  error: { margin: '4px 0 0', fontSize: 13, color: '#c0392b', fontWeight: 500 },
  button: { marginTop: 16, height: 44, borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  buttonBusy: { opacity: 0.7, cursor: 'default' },
  foot: { margin: '14px 0 0', fontSize: 11.5, color: 'var(--muted, #8a8a8a)', textAlign: 'center', lineHeight: 1.5 },
}
